import React, { useState } from "react";

function formatAmount(amount, currency) {
  if (amount == null) return "TBC";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currency || "AUD",
  }).format(amount);
}

export default function ProposalPage({ proposal }) {
  const {
    meta,
    hero,
    goals,
    sections,
    pricing,
    maintenancePlans,
    faqs,
    acceptance,
  } = proposal;

  const [showTerms, setShowTerms] = useState(false);

  const sectionIds = [
    { id: "overview", label: "Overview" },
    { id: "goals", label: "Goals" },
    { id: "scope", label: "Scope" },
    { id: "process", label: "Process" },
    { id: "timeline", label: "Timeline" },
    { id: "pricing", label: "Investment" },
    { id: "maintenance", label: "Maintenance" },
    { id: "faqs", label: "FAQs" },
    { id: "accept", label: "Next steps" },
  ];

  const totalAmount =
    pricing?.items
      ?.filter((i) => typeof i.amount === "number")
      ?.reduce((sum, i) => sum + i.amount, 0) || 0;

  const depositAmount = totalAmount * 0.5;

  return (
    <main className="proposal-page">
      <header className="proposal-hero">
        <div className="proposal-hero-inner">
          <p className="proposal-hero-eyebrow">{hero.eyebrow}</p>
          <h1 className="proposal-hero-heading">{hero.heading}</h1>
          <p className="proposal-hero-client">
            For <span>{meta.clientName}</span> · {meta.date}
          </p>
          <p className="proposal-hero-intro">{hero.intro}</p>

          <div className="proposal-hero-contact">
            <div>
              <p className="proposal-hero-contact-label">Prepared by</p>
              <p className="proposal-hero-contact-value">
                {hero.contact.contactName} · {hero.contact.agencyName}
              </p>
            </div>
            <div>
              <p className="proposal-hero-contact-label">Contact</p>
              <p className="proposal-hero-contact-value">
                <a href={`mailto:${hero.contact.email}`}>
                  {hero.contact.email}
                </a>
                {" · "}
                <a href={`tel:${hero.contact.phone.replace(/\s+/g, "")}`}>
                  {hero.contact.phone}
                </a>
              </p>
            </div>
          </div>

          <div className="proposal-meta-strip">
            <span>Quote: {meta.quoteNumber}</span>
            <span>Site: {meta.siteDomain}</span>
          </div>
        </div>

        <nav className="proposal-nav">
          <ul>
            {sectionIds.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.label}</a>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <section id="overview" className="proposal-section">
        <h2>Project overview</h2>
        {meta.summary.map((p, idx) => (
          <p key={idx}>{p}</p>
        ))}
      </section>

      {goals && goals.length > 0 && (
        <section id="goals" className="proposal-section">
          <h2>Goals</h2>
          <ul className="proposal-list">
            {goals.map((goal, idx) => (
              <li key={idx}>{goal}</li>
            ))}
          </ul>
        </section>
      )}

      {sections &&
        sections.map((section) => (
          <section
            key={section.id}
            id={section.id}
            className="proposal-section"
          >
            <h2>{section.title}</h2>
            {section.paragraphs &&
              section.paragraphs.map((p, idx) => <p key={idx}>{p}</p>)}
            {section.bullets && (
              <ul className="proposal-list">
                {section.bullets.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            )}
            {section.steps && (
              <div className="proposal-steps">
                {section.steps.map((step, idx) => (
                  <div key={idx} className="proposal-step-card">
                    <h3>{step.label}</h3>
                    <p>{step.detail}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}

      {pricing && (
        <section id="pricing" className="proposal-section">
          <h2>Investment</h2>
          {pricing.disclaimer && (
            <p className="proposal-small">{pricing.disclaimer}</p>
          )}

          <div className="proposal-pricing-table">
            {pricing.items.map((item, idx) => (
              <div key={idx} className="proposal-pricing-row">
                <div>
                  <p className="proposal-pricing-label">{item.label}</p>
                  {item.description && (
                    <p className="proposal-pricing-desc">
                      {item.description}
                    </p>
                  )}
                </div>
                <div className="proposal-pricing-amount">
                  {formatAmount(item.amount, pricing.currency)}
                </div>
              </div>
            ))}
          </div>

          {pricing.paymentSchedule && pricing.paymentSchedule.length > 0 && (
            <div className="proposal-payment-schedule">
              <h3>Indicative payment schedule</h3>
              <ul className="proposal-list">
                {pricing.paymentSchedule.map((ps, idx) => (
                  <li key={idx}>
                    <strong>{ps.label}:</strong>{" "}
                    {ps.percentage != null ? `${ps.percentage}%` : ""}{" "}
                    {ps.detail && `· ${ps.detail}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {maintenancePlans && maintenancePlans.length > 0 && (
        <section id="maintenance" className="proposal-section">
          <h2>Maintenance & support</h2>
          <div className="proposal-maintenance-grid">
            {maintenancePlans.map((plan, idx) => (
              <article key={idx} className="proposal-maintenance-card">
                <h3>{plan.name}</h3>
                <p className="proposal-maintenance-price">
                  {plan.priceLabel}
                </p>
                <p className="proposal-maintenance-tagline">
                  {plan.tagline}
                </p>
                <ul className="proposal-list">
                  {plan.includes.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>
      )}

      {faqs && faqs.length > 0 && (
        <section id="faqs" className="proposal-section">
          <h2>FAQs</h2>
          <div className="proposal-faqs">
            {faqs.map((faq, idx) => (
              <details key={idx} className="proposal-faq">
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {acceptance && (
        <section id="accept" className="proposal-section proposal-accept">
          <h2>{acceptance.heading}</h2>
          <p>{acceptance.blurb}</p>

          <ul className="proposal-list proposal-small">
            {acceptance.terms.map((term, idx) => (
              <li key={idx}>{term}</li>
            ))}
          </ul>

          <div className="proposal-payment-box">
            <h3>Payment details</h3>

            <p>
              <strong>Deposit due (50%):</strong>{" "}
              {depositAmount > 0
                ? new Intl.NumberFormat("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  }).format(depositAmount)
                : "TBC"}
            </p>

            <p>
              <strong>Bank name:</strong> ANZ
            </p>
            <p>
              <strong>Account name:</strong> 2mer Pathways
            </p>
            <p>
              <strong>BSB:</strong> 015 214
            </p>
            <p>
              <strong>Account number:</strong> 802397505
            </p>
          </div>

      <form
  className="proposal-accept-form"
  action="https://formspree.io/f/xanrpzrb"
  method="POST"
>
  <input
    type="hidden"
    name="proposal"
    value={meta.title || meta.quoteNumber || "2merative Proposal"}
  />
  <input type="hidden" name="clientName" value={meta.clientName} />
  <input type="hidden" name="quoteNumber" value={meta.quoteNumber} />
  <input type="hidden" name="siteDomain" value={meta.siteDomain} />
  <input
    type="hidden"
    name="_subject"
    value={`Proposal accepted: ${meta.clientName} – ${meta.quoteNumber}`}
  />
  {/* Use the new ThankYou route */}
  <input
    type="hidden"
    name="_next"
    value="/thankyou"
  />

  <div className="proposal-form-row">
    <label>
      Your name
      <input type="text" name="name" autoComplete="name" required />
    </label>
    <label>
      Role / position
      <input type="text" name="role" required />
    </label>
  </div>

  <div className="proposal-form-row">
    <label>
      Email
      <input
        type="email"
        name="email"
        autoComplete="email"
        required
      />
    </label>
    <label>
      Company
      <input
        type="text"
        name="company"
        defaultValue={meta.clientName}
      />
    </label>
  </div>

  <label className="proposal-checkbox">
    <input type="checkbox" name="authorised" value="yes" required />{" "}
    <span>
      I am authorised to approve this proposal on behalf of{" "}
      {meta.clientName}.
    </span>
  </label>

  <div className="proposal-actions">
    <button type="submit" className="proposal-accept-button">
      Accept proposal
    </button>
    <button
      type="button"
      className="proposal-terms-button"
      onClick={() => setShowTerms((v) => !v)}
    >
      Terms & conditions
    </button>
  </div>
</form>


          {showTerms && (
            <div className="proposal-terms">
              <h3>Terms & conditions</h3>

              <p>
                By clicking the <strong>"Accept proposal"</strong> button you
                confirm that you are authorised to act on behalf of{" "}
                {meta.clientName} and you are entering into a contractual
                agreement with <strong>2merative Web Design</strong>.
              </p>

              <p>
                This agreement is entered into in good faith by both parties,
                with the shared intention of delivering the project described in
                this proposal.
              </p>

              <ul className="proposal-list">
                <li>
                  Once this proposal is accepted, the project is considered
                  committed. All agreed payments (including the deposit and
                  subsequent instalments) remain due from the accepting party,
                  even if you later choose not to proceed to full project
                  completion.
                </li>

                <li>
                  All projects are quoted based on an estimated time to deliver,
                  assuming timely responses, content supply and approvals from
                  you. Significant delays caused by non-responsiveness or slow
                  provision of requested content, feedback or access may require
                  a revision of the overall project cost and/or timeline.
                </li>

                <li>
                  Material changes to scope requested after acceptance (such as
                  additional pages, new features or redesigned sections) may be
                  treated as variations and quoted separately before work
                  proceeds.
                </li>

                <li>
                  If the project stalls for an extended period due to client
                  non-responsiveness, 2merative Web Design may, after reasonable
                  attempts to re-engage, close the project and issue an invoice
                  for the <strong>full contracted project amount</strong>. This
                  includes the remaining balance regardless of the stage of
                  completion, as the project timeline, development resources and
                  delivery capacity have been reserved exclusively for the
                  client.
                </li>

                <li>
                  Payment terms for invoices are as stated on each invoice. Late
                  payment may pause further work until accounts are brought up
                  to date.
                </li>
              </ul>

              <p className="proposal-small">
                These terms protect both parties and ensure that the time and
                resources reserved for your project are respected. If you have
                any questions or concerns about these terms, please contact us
                before accepting the proposal.
              </p>
            </div>
          )}
        </section>
      )}
    </main>
  );
}
